import { useState, type FormEvent } from "react";
import { css } from "glamor";
import { setToken } from "../auth";

interface LoginPageProps {
  onLogin: () => void;
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let domain = import.meta.env.VITE_PLH_API_URL || "";
      if (domain.endsWith("/")) domain = domain.slice(0, -1);

      const res = await fetch(`${domain}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || "Invalid credentials");
      }

      const { token } = await res.json();
      if (token) {
        setToken(token);
      } else {
        sessionStorage.setItem("plh_auth_disabled", "true");
      }
      onLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div {...styles.wrapper}>
      <form {...styles.card} onSubmit={handleSubmit}>
        <h1 {...styles.title}>PLH Dashboard</h1>
        <p {...styles.subtitle}>Sign in to continue</p>

        {error && <div {...styles.error}>{error}</div>}

        <label {...styles.label}>
          Username
          <input
            {...styles.input}
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </label>

        <label {...styles.label}>
          Password
          <input
            {...styles.input}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>

        <button {...styles.button} type="submit" disabled={loading}>
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </div>
  );
}

const styles = {
  wrapper: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "#f5f6f8",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  }),
  card: css({
    background: "#fff",
    borderRadius: 12,
    padding: "40px 36px 36px",
    width: 360,
    maxWidth: "90vw",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  }),
  title: css({
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "#353535",
    textAlign: "center" as const,
  }),
  subtitle: css({
    margin: 0,
    fontSize: 14,
    color: "rgba(0,0,0,0.54)",
    textAlign: "center" as const,
    marginBottom: 8,
  }),
  error: css({
    background: "rgba(255, 50, 70, 0.08)",
    color: "#dc0029",
    fontSize: 13,
    padding: "10px 14px",
    borderRadius: 8,
  }),
  label: css({
    display: "flex",
    flexDirection: "column",
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    color: "#353535",
  }),
  input: css({
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 400,
    outline: "none",
    transition: "border-color 0.15s",
    ":focus": {
      borderColor: "#5551FF",
    },
  }),
  button: css({
    marginTop: 8,
    padding: "12px 0",
    background: "#5551FF",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.15s",
    ":hover": {
      background: "#4440dd",
    },
    ":disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  }),
};
